import { FormEvent, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { apiRequest } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import PublicSiteLayout from "@/components/landing/PublicSiteLayout";
import {
  ArrowRight,
  BadgeCheck,
  BookOpenText,
  Building2,
  Check,
  CircleDollarSign,
  Factory,
  Handshake,
  Layers3,
  Megaphone,
  Network,
  ShieldCheck,
} from "lucide-react";

type AffiliateFormState = {
  fullName: string;
  companyName: string;
  companyWebsite: string;
  email: string;
  partnerType: string;
  audience: string;
  audienceSize: string;
  referralMotion: string;
  expectedMonthlyReferrals: string;
  notes: string;
};

const initialFormState: AffiliateFormState = {
  fullName: "",
  companyName: "",
  companyWebsite: "",
  email: "",
  partnerType: "referral-affiliate",
  audience: "contractors",
  audienceSize: "not-specified",
  referralMotion: "client-referrals",
  expectedMonthlyReferrals: "0-5",
  notes: "",
};

export default function Affiliate() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [form, setForm] = useState<AffiliateFormState>(initialFormState);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const scrollTo = (id: string) => {
    document
      .getElementById(id)
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const submitApplication = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitting(true);
    setSubmitted(false);

    void (async () => {
      try {
        await apiRequest<{ ok: true }>("/affiliate/apply", {
          method: "POST",
          auth: false,
          body: {
            fullName: form.fullName.trim(),
            companyName: form.companyName.trim() || undefined,
            companyWebsite: form.companyWebsite.trim() || undefined,
            email: form.email.trim(),
            partnerType: form.partnerType || undefined,
            audience: form.audience || undefined,
            audienceSize: form.audienceSize || undefined,
            referralMotion: form.referralMotion || undefined,
            expectedMonthlyReferrals:
              form.expectedMonthlyReferrals || undefined,
            notes: form.notes.trim() || undefined,
          },
        });
        setSubmitted(true);
        setForm(initialFormState);
      } catch (error) {
        toast({
          title: t("affiliate.apply.errorTitle"),
          description:
            error instanceof Error ? error.message : t("common.tryAgain"),
          variant: "destructive",
        });
      } finally {
        setSubmitting(false);
      }
    })();
  };

  const fitCards = useMemo(
    () => [
      {
        icon: Layers3,
        title: t("affiliate.why.cards.workflow.title"),
        body: t("affiliate.why.cards.workflow.body"),
      },
      {
        icon: CircleDollarSign,
        title: t("affiliate.why.cards.recurringRevenue.title"),
        body: t("affiliate.why.cards.recurringRevenue.body"),
      },
      {
        icon: ShieldCheck,
        title: t("affiliate.why.cards.selectiveProgram.title"),
        body: t("affiliate.why.cards.selectiveProgram.body"),
      },
    ],
    [t],
  );

  const whoItsForCards = useMemo(
    () => [
      {
        icon: Building2,
        title: t("affiliate.who.agencies.title"),
        body: t("affiliate.who.agencies.body"),
      },
      {
        icon: Handshake,
        title: t("affiliate.who.consultants.title"),
        body: t("affiliate.who.consultants.body"),
      },
      {
        icon: Megaphone,
        title: t("affiliate.who.communitiesCreators.title"),
        body: t("affiliate.who.communitiesCreators.body"),
      },
      {
        icon: Factory,
        title: t("affiliate.who.industryPartners.title"),
        body: t("affiliate.who.industryPartners.body"),
      },
      {
        icon: Network,
        title: t("affiliate.who.strategicChannels.title"),
        body: t("affiliate.who.strategicChannels.body"),
      },
    ],
    [t],
  );

  const partnerModels = useMemo(
    () => [
      {
        eyebrow: "01",
        title: t("affiliate.models.referralPartner.title"),
        body: t("affiliate.models.referralPartner.body"),
        bestFor: t("affiliate.models.referralPartner.bestFor"),
        referralStyle: t("affiliate.models.referralPartner.referralStyle"),
        support: t("affiliate.models.referralPartner.support"),
      },
      {
        eyebrow: "02",
        title: t("affiliate.models.agencyConsultant.title"),
        body: t("affiliate.models.agencyConsultant.body"),
        bestFor: t("affiliate.models.agencyConsultant.bestFor"),
        referralStyle: t("affiliate.models.agencyConsultant.referralStyle"),
        support: t("affiliate.models.agencyConsultant.support"),
      },
      {
        eyebrow: "03",
        title: t("affiliate.models.strategicPartner.title"),
        body: t("affiliate.models.strategicPartner.body"),
        bestFor: t("affiliate.models.strategicPartner.bestFor"),
        referralStyle: t("affiliate.models.strategicPartner.referralStyle"),
        support: t("affiliate.models.strategicPartner.support"),
      },
    ],
    [t],
  );
  const faqItems = useMemo(
    () => [
      {
        q: t("affiliate.faq.items.payout.q"),
        a: t("affiliate.faq.items.payout.a"),
      },
      {
        q: t("affiliate.faq.items.promoCodes.q"),
        a: t("affiliate.faq.items.promoCodes.a"),
      },
      {
        q: t("affiliate.faq.items.existingClients.q"),
        a: t("affiliate.faq.items.existingClients.a"),
      },
      {
        q: t("affiliate.faq.items.minimumVolume.q"),
        a: t("affiliate.faq.items.minimumVolume.a"),
      },
      {
        q: t("affiliate.faq.items.freeOrTrial.q"),
        a: t("affiliate.faq.items.freeOrTrial.a"),
      },
      {
        q: t("affiliate.faq.items.refunds.q"),
        a: t("affiliate.faq.items.refunds.a"),
      },
      {
        q: t("affiliate.faq.items.varyByPartner.q"),
        a: t("affiliate.faq.items.varyByPartner.a"),
      },
      {
        q: t("affiliate.faq.items.manualReview.q"),
        a: t("affiliate.faq.items.manualReview.a"),
      },
      {
        q: t("affiliate.faq.items.customTerms.q"),
        a: t("affiliate.faq.items.customTerms.a"),
      },
    ],
    [t],
  );

  return (
    <PublicSiteLayout>
      <section className="relative overflow-hidden border-b border-sidebar-border bg-[linear-gradient(130deg,hsl(var(--sidebar-background))_0%,#081836_38%,#0C1F4F_100%)] py-16 lg:py-24">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(900px_520px_at_0%_-10%,rgba(243,140,22,0.18),transparent)]" />
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(880px_400px_at_100%_0%,rgba(94,150,255,0.16),transparent)]" />
        <div className="container mx-auto grid gap-8 px-4 text-white lg:grid-cols-[minmax(0,1.1fr)_minmax(320px,0.9fr)] lg:items-center lg:gap-14 lg:px-8">
          <div style={{ color: "#FFFFFF" }}>
            <div className="inline-flex items-center rounded-full border border-accent/70 bg-accent/10 px-2.5 py-0.5 text-xs font-semibold text-white">
              {t("affiliate.title")}
            </div>
            <h1 className="mt-4 max-w-3xl text-3xl font-bold tracking-tight text-white md:text-5xl">
              {t("affiliate.hero.title")}
            </h1>
            <p className="mt-5 max-w-2xl text-base leading-7 text-white/82 md:text-lg">
              {t("affiliate.hero.subtitle")}
            </p>
            <p className="mt-4 max-w-2xl text-sm leading-6 text-white/72">
              {t("affiliate.hero.termsNote")}
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Button
                className="bg-accent text-accent-foreground hover:bg-accent/90"
                onClick={() => scrollTo("affiliate-apply")}
              >
                {t("affiliate.hero.applyCta")}
              </Button>
              <button
                type="button"
                className="inline-flex h-10 items-center justify-center rounded-md border border-white/25 bg-white/5 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-white/10"
                onClick={() => scrollTo("partner-how-it-works")}
              >
                {t("affiliate.hero.secondaryCta")}
              </button>
            </div>
          </div>

          <div
            className="rounded-[1.8rem] border border-white/12 bg-white/[0.05] p-6 text-white shadow-[0_30px_90px_-48px_rgba(0,0,0,0.7)] backdrop-blur-sm lg:p-7"
            style={{ color: "#FFFFFF" }}
          >
            <div className="flex items-center gap-2 text-white">
              <BadgeCheck className="h-5 w-5 text-accent" />
              <h2 className="text-lg font-semibold text-white">
                {t("affiliate.hero.cardTitle")}
              </h2>
            </div>
            <ul className="mt-5 space-y-3">
              {[
                t("affiliate.hero.cardPoint1"),
                t("affiliate.hero.cardPoint2"),
                t("affiliate.hero.cardPoint3"),
                t("affiliate.hero.cardPoint4"),
              ].map((item) => (
                <li
                  key={item}
                  className="flex items-start gap-2 text-sm leading-6 text-white/78"
                >
                  <Check className="mt-1 h-4 w-4 shrink-0 text-accent" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <div className="mt-6 rounded-2xl border border-white/10 bg-[#08142E] px-4 py-4">
              <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-accent/90">
                {t("affiliate.hero.reviewLabel")}
              </p>
              <p className="mt-2 text-sm leading-6 text-white/72">
                {t("affiliate.hero.reviewBody")}
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#F6FAFF] py-16 lg:py-20">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-2xl">
            <p className="text-sm font-semibold uppercase tracking-[0.16em] text-accent">
              {t("affiliate.why.eyebrow")}
            </p>
            <h2 className="mt-3 text-2xl font-bold text-primary md:text-3xl">
              {t("affiliate.why.title")}
            </h2>
            <p className="mt-4 text-base leading-7 text-muted-foreground">
              {t("affiliate.why.subtitle")}
            </p>
          </div>

          <div className="mt-8 grid gap-4 lg:grid-cols-3">
            {fitCards.map((card) => (
              <article
                key={card.title}
                className="rounded-[1.5rem] border border-[#D9E3F6] bg-white p-6 shadow-[0_20px_60px_-45px_rgba(12,31,79,0.32)] transition-all duration-300 ease-out hover:-translate-y-1 hover:border-[#C7D6F4] hover:shadow-[0_28px_80px_-42px_rgba(12,31,79,0.35)]"
              >
                <card.icon className="h-5 w-5 text-accent" />
                <h3 className="mt-4 text-lg font-semibold text-primary">
                  {card.title}
                </h3>
                <p className="mt-2 text-sm leading-6 text-muted-foreground">
                  {card.body}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="border-y border-border/50 bg-white py-16 lg:py-20">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-2xl">
            <p className="text-sm font-semibold uppercase tracking-[0.16em] text-accent">
              {t("affiliate.who.eyebrow")}
            </p>
            <h2 className="mt-3 text-2xl font-bold text-primary md:text-3xl">
              {t("affiliate.who.title")}
            </h2>
            <p className="mt-4 text-base leading-7 text-muted-foreground">
              {t("affiliate.who.subtitle")}
            </p>
          </div>

          <div className="mt-8 grid gap-4 md:grid-cols-2 xl:grid-cols-5">
            {whoItsForCards.map((card) => (
              <article
                key={card.title}
                className="rounded-[1.4rem] border border-border/70 bg-[#FCFDFE] p-5 shadow-[0_16px_40px_-30px_rgba(12,31,79,0.18)] transition-all duration-300 ease-out hover:-translate-y-1 hover:border-[#D3DDEC] hover:shadow-[0_26px_70px_-38px_rgba(12,31,79,0.28)]"
              >
                <card.icon className="h-5 w-5 text-accent" />
                <h3 className="mt-3 text-base font-semibold text-primary">
                  {card.title}
                </h3>
                <p className="mt-2 text-sm leading-6 text-muted-foreground">
                  {card.body}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section
        id="partner-how-it-works"
        className="relative overflow-hidden bg-background py-16 lg:py-20"
      >
        <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-border to-transparent" />
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-2xl">
            <p className="text-sm font-semibold uppercase tracking-[0.16em] text-accent">
              {t("affiliate.how.eyebrow")}
            </p>
            <h2 className="mt-3 text-2xl font-bold text-primary md:text-3xl">
              {t("affiliate.how.title")}
            </h2>
            <p className="mt-4 text-base leading-7 text-muted-foreground">
              {t("affiliate.how.subtitle")}
            </p>
          </div>

          <div className="mt-10 grid gap-4 lg:grid-cols-4">
            {[
              {
                number: "01",
                title: t("affiliate.how.step1.title"),
                body: t("affiliate.how.step1.body"),
              },
              {
                number: "02",
                title: t("affiliate.how.step2.title"),
                body: t("affiliate.how.step2.body"),
              },
              {
                number: "03",
                title: t("affiliate.how.step3.title"),
                body: t("affiliate.how.step3.body"),
              },
              {
                number: "04",
                title: t("affiliate.how.step4.title"),
                body: t("affiliate.how.step4.body"),
              },
            ].map((step) => (
              <article
                key={step.number}
                className="rounded-[1.5rem] border border-border/70 bg-[#FCFDFE] px-5 py-6 shadow-[0_16px_40px_-30px_rgba(12,31,79,0.28)] transition-all duration-300 ease-out hover:-translate-y-1 hover:border-[#D2DCF0] hover:shadow-[0_28px_80px_-42px_rgba(12,31,79,0.32)]"
              >
                <div className="flex h-11 w-11 items-center justify-center rounded-full border border-accent/30 bg-accent text-sm font-bold text-accent-foreground">
                  {step.number}
                </div>
                <h3 className="mt-5 text-lg font-semibold text-primary">
                  {step.title}
                </h3>
                <p className="mt-2 text-sm leading-6 text-muted-foreground">
                  {step.body}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden border-y border-white/10 bg-[linear-gradient(140deg,#071633_0%,#0C1F4F_58%,#132B66_100%)] py-16 lg:py-20">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(700px_320px_at_100%_0%,rgba(243,140,22,0.22),transparent)]" />
        <div className="container mx-auto px-4 text-white lg:px-8">
          <div className="max-w-2xl" style={{ color: "#FFFFFF" }}>
            <p className="text-sm font-semibold uppercase tracking-[0.16em] text-accent/90">
              {t("affiliate.models.eyebrow")}
            </p>
            <h2 className="mt-3 text-2xl font-bold text-white md:text-3xl">
              {t("affiliate.models.title")}
            </h2>
            <p className="mt-4 text-base leading-7 text-white/74">
              {t("affiliate.models.subtitle")}
            </p>
          </div>
          <div className="mt-8 grid gap-4 lg:grid-cols-3">
            {partnerModels.map((model, index) => (
              <article
                key={model.title}
                className={`rounded-[1.6rem] border p-6 text-white shadow-[0_28px_80px_-48px_rgba(0,0,0,0.72)] backdrop-blur-sm transition-all duration-300 ease-out hover:-translate-y-1 hover:shadow-[0_36px_100px_-46px_rgba(0,0,0,0.78)] ${
                  index === 1
                    ? "border-accent/35 bg-[linear-gradient(180deg,rgba(243,140,22,0.16),rgba(255,255,255,0.06))] hover:border-accent/55"
                    : "border-white/10 bg-white/[0.05] hover:border-white/20"
                }`}
                style={{ color: "#FFFFFF" }}
              >
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-accent">
                  {model.eyebrow}
                </p>
                <h3 className="mt-4 text-xl font-semibold text-white">
                  {model.title}
                </h3>
                <p className="mt-3 text-sm leading-6 text-white/74">
                  {model.body}
                </p>

                <div className="mt-5 space-y-4 border-t border-white/10 pt-5">
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-white/45">
                      {t("affiliate.models.bestForLabel")}
                    </p>
                    <p className="mt-1 text-sm leading-6 text-white/82">
                      {model.bestFor}
                    </p>
                  </div>

                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-white/45">
                      {t("affiliate.models.referralStyleLabel")}
                    </p>
                    <p className="mt-1 text-sm leading-6 text-white/82">
                      {model.referralStyle}
                    </p>
                  </div>

                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-white/45">
                      {t("affiliate.models.supportLabel")}
                    </p>
                    <p className="mt-1 text-sm leading-6 text-white/82">
                      {model.support}
                    </p>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#F9FBFF] py-16 lg:py-20">
        <div className="container mx-auto grid gap-8 px-4 lg:grid-cols-[minmax(0,1fr)_minmax(320px,0.9fr)] lg:items-start lg:px-8">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.16em] text-accent">
              {t("affiliate.payouts.eyebrow")}
            </p>
            <h2 className="mt-3 text-2xl font-bold text-primary md:text-3xl">
              {t("affiliate.payouts.title")}
            </h2>
            <p className="mt-4 text-base leading-7 text-muted-foreground">
              {t("affiliate.payouts.subtitle")}
            </p>
            <ul className="mt-6 space-y-3">
              {[
                t("affiliate.payouts.point1"),
                t("affiliate.payouts.point2"),
                t("affiliate.payouts.point3"),
                t("affiliate.payouts.point4"),
                t("affiliate.payouts.point5"),
              ].map((item) => (
                <li
                  key={item}
                  className="flex items-start gap-2 text-sm leading-6 text-muted-foreground"
                >
                  <Check className="mt-1 h-4 w-4 shrink-0 text-accent" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="space-y-4">
            <article className="rounded-[1.5rem] border border-[#D7E1F4] bg-white p-6 shadow-[0_20px_60px_-45px_rgba(12,31,79,0.28)] transition-all duration-300 ease-out hover:-translate-y-1 hover:border-[#C8D7F2] hover:shadow-[0_28px_80px_-42px_rgba(12,31,79,0.34)]">
              <div className="flex items-center gap-2">
                <BookOpenText className="h-5 w-5 text-accent" />
                <h3 className="text-lg font-semibold text-primary">
                  {t("affiliate.promo.title")}
                </h3>
              </div>
              <ul className="mt-4 space-y-3">
                {[
                  t("affiliate.promo.point1"),
                  t("affiliate.promo.point2"),
                  t("affiliate.promo.point3"),
                ].map((item) => (
                  <li
                    key={item}
                    className="flex items-start gap-2 text-sm leading-6 text-muted-foreground"
                  >
                    <Check className="mt-1 h-4 w-4 shrink-0 text-accent" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </article>

            <article className="rounded-[1.5rem] border border-[#D7E1F4] bg-[linear-gradient(180deg,#FFFFFF_0%,#F7FAFF_100%)] p-6 shadow-[0_20px_60px_-45px_rgba(12,31,79,0.28)] transition-all duration-300 ease-out hover:-translate-y-1 hover:border-[#C8D7F2] hover:shadow-[0_28px_80px_-42px_rgba(12,31,79,0.34)]">
              <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-accent">
                {t("affiliate.review.eyebrow")}
              </p>
              <h3 className="mt-3 text-lg font-semibold text-primary">
                {t("affiliate.review.title")}
              </h3>
              <p className="mt-2 text-sm leading-6 text-muted-foreground">
                {t("affiliate.review.body")}
              </p>
            </article>
          </div>
        </div>
      </section>

      <section className="bg-background py-16 lg:py-20">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-2xl">
            <p className="text-sm font-semibold uppercase tracking-[0.16em] text-accent">
              {t("affiliate.faq.eyebrow")}
            </p>
            <h2 className="mt-3 text-2xl font-bold text-primary md:text-3xl">
              {t("affiliate.faq.title")}
            </h2>
            <p className="mt-4 text-base leading-7 text-muted-foreground">
              {t("affiliate.faq.subtitle")}
            </p>
          </div>
          <div className="mt-8 rounded-[1.5rem] border bg-card p-5 shadow-sm">
            <Accordion type="single" collapsible className="w-full">
              {faqItems.map((item, index) => (
                <AccordionItem key={item.q} value={`affiliate-faq-${index}`}>
                  <AccordionTrigger className="text-left text-base font-semibold text-primary">
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm leading-6 text-muted-foreground">
                    {item.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      <section
        id="affiliate-apply"
        className="border-y border-border/50 bg-gradient-to-b from-[#F7FAFF] to-[#FDFEFF] py-16 lg:py-20"
      >
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-2xl">
            <p className="text-sm font-semibold uppercase tracking-[0.16em] text-accent">
              {t("affiliate.apply.eyebrow")}
            </p>
            <h2 className="mt-3 text-2xl font-bold text-primary md:text-3xl">
              {t("affiliate.apply.title")}
            </h2>
            <p className="mt-4 text-base leading-7 text-muted-foreground">
              {t("affiliate.apply.subtitle")}
            </p>
          </div>

          <div className="mt-8 grid gap-6 lg:grid-cols-[minmax(0,1.1fr)_340px] lg:items-start">
            <form
              className="overflow-hidden rounded-[1.75rem] border border-[#D7E1F4] bg-white shadow-[0_28px_80px_-50px_rgba(12,31,79,0.35)]"
              onSubmit={submitApplication}
            >
              <div className="border-b border-[#E5ECF8] bg-[linear-gradient(180deg,#FFFFFF_0%,#F7FAFF_100%)] px-5 py-5 lg:px-7">
                <p className="text-sm text-muted-foreground">
                  {t("affiliate.apply.cardIntro")}
                </p>
              </div>

              <div className="grid gap-4 px-5 py-5 md:grid-cols-2 lg:px-7 lg:py-7">
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-primary">
                    {t("affiliate.apply.fullName")}
                    <span className="ml-1 text-[#F38C16]">*</span>
                  </label>
                  <Input
                    className="h-11 rounded-xl border-[#D6E0F3] bg-[#FCFDFE] px-3.5"
                    value={form.fullName}
                    onChange={(event) =>
                      setForm((prev) => ({
                        ...prev,
                        fullName: event.target.value,
                      }))
                    }
                    required
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-primary">
                    {t("affiliate.apply.companyName")}
                  </label>
                  <Input
                    className="h-11 rounded-xl border-[#D6E0F3] bg-[#FCFDFE] px-3.5"
                    value={form.companyName}
                    onChange={(event) =>
                      setForm((prev) => ({
                        ...prev,
                        companyName: event.target.value,
                      }))
                    }
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-primary">
                    {t("affiliate.apply.companyWebsite")}
                  </label>
                  <Input
                    className="h-11 rounded-xl border-[#D6E0F3] bg-[#FCFDFE] px-3.5"
                    value={form.companyWebsite}
                    onChange={(event) =>
                      setForm((prev) => ({
                        ...prev,
                        companyWebsite: event.target.value,
                      }))
                    }
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-primary">
                    {t("affiliate.apply.email")}
                    <span className="ml-1 text-[#F38C16]">*</span>
                  </label>
                  <Input
                    type="email"
                    className="h-11 rounded-xl border-[#D6E0F3] bg-[#FCFDFE] px-3.5"
                    value={form.email}
                    onChange={(event) =>
                      setForm((prev) => ({
                        ...prev,
                        email: event.target.value,
                      }))
                    }
                    required
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-primary">
                    {t("affiliate.apply.partnerType")}
                  </label>
                  <select
                    className="h-11 w-full rounded-xl border border-[#D6E0F3] bg-[#FCFDFE] px-3.5 text-sm"
                    value={form.partnerType}
                    onChange={(event) =>
                      setForm((prev) => ({
                        ...prev,
                        partnerType: event.target.value,
                      }))
                    }
                  >
                    <option value="referral-affiliate">
                      {t(
                        "affiliate.apply.partnerTypeOptions.referralAffiliate",
                      )}
                    </option>
                    <option value="agency">
                      {t("affiliate.apply.partnerTypeOptions.agency")}
                    </option>
                    <option value="consultant">
                      {t("affiliate.apply.partnerTypeOptions.consultant")}
                    </option>
                    <option value="community-creator">
                      {t("affiliate.apply.partnerTypeOptions.communityCreator")}
                    </option>
                    <option value="industry-association">
                      {t(
                        "affiliate.apply.partnerTypeOptions.industryAssociation",
                      )}
                    </option>
                    <option value="lead-gen-platform">
                      {t("affiliate.apply.partnerTypeOptions.leadGenPlatform")}
                    </option>
                    <option value="strategic-high-volume">
                      {t(
                        "affiliate.apply.partnerTypeOptions.strategicHighVolume",
                      )}
                    </option>
                    <option value="other">
                      {t("affiliate.apply.partnerTypeOptions.other")}
                    </option>
                  </select>
                </div>

                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-primary">
                    {t("affiliate.apply.audience")}
                  </label>
                  <select
                    className="h-11 w-full rounded-xl border border-[#D6E0F3] bg-[#FCFDFE] px-3.5 text-sm"
                    value={form.audience}
                    onChange={(event) =>
                      setForm((prev) => ({
                        ...prev,
                        audience: event.target.value,
                      }))
                    }
                  >
                    <option value="contractors">
                      {t("affiliate.apply.audienceOptions.contractors")}
                    </option>
                    <option value="field-service-businesses">
                      {t(
                        "affiliate.apply.audienceOptions.fieldServiceBusinesses",
                      )}
                    </option>
                    <option value="home-service-businesses">
                      {t(
                        "affiliate.apply.audienceOptions.homeServiceBusinesses",
                      )}
                    </option>
                    <option value="commercial-service-providers">
                      {t(
                        "affiliate.apply.audienceOptions.commercialServiceProviders",
                      )}
                    </option>
                    <option value="property-facility-operations">
                      {t(
                        "affiliate.apply.audienceOptions.propertyFacilityOperations",
                      )}
                    </option>
                    <option value="mixed">
                      {t("affiliate.apply.audienceOptions.mixed")}
                    </option>
                    <option value="other">
                      {t("affiliate.apply.audienceOptions.other")}
                    </option>
                  </select>
                </div>

                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-primary">
                    {t("affiliate.apply.audienceSize")}
                  </label>
                  <select
                    className="h-11 w-full rounded-xl border border-[#D6E0F3] bg-[#FCFDFE] px-3.5 text-sm"
                    value={form.audienceSize}
                    onChange={(event) =>
                      setForm((prev) => ({
                        ...prev,
                        audienceSize: event.target.value,
                      }))
                    }
                  >
                    <option value="not-specified">
                      {t("affiliate.apply.audienceSizeOptions.notSpecified")}
                    </option>
                    <option value="under-1000">
                      {t("affiliate.apply.audienceSizeOptions.under1000")}
                    </option>
                    <option value="1000-5000">
                      {t("affiliate.apply.audienceSizeOptions.1000to5000")}
                    </option>
                    <option value="5000-20000">
                      {t("affiliate.apply.audienceSizeOptions.5000to20000")}
                    </option>
                    <option value="20000+">
                      {t("affiliate.apply.audienceSizeOptions.20000Plus")}
                    </option>
                  </select>
                </div>

                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-primary">
                    {t("affiliate.apply.referralMotion")}
                  </label>
                  <select
                    className="h-11 w-full rounded-xl border border-[#D6E0F3] bg-[#FCFDFE] px-3.5 text-sm"
                    value={form.referralMotion}
                    onChange={(event) =>
                      setForm((prev) => ({
                        ...prev,
                        referralMotion: event.target.value,
                      }))
                    }
                  >
                    <option value="newsletter-content">
                      {t(
                        "affiliate.apply.referralMotionOptions.newsletterContent",
                      )}
                    </option>
                    <option value="client-referrals">
                      {t(
                        "affiliate.apply.referralMotionOptions.clientReferrals",
                      )}
                    </option>
                    <option value="consulting-implementation">
                      {t(
                        "affiliate.apply.referralMotionOptions.consultingImplementation",
                      )}
                    </option>
                    <option value="community-events">
                      {t(
                        "affiliate.apply.referralMotionOptions.communityEvents",
                      )}
                    </option>
                    <option value="lead-distribution">
                      {t(
                        "affiliate.apply.referralMotionOptions.leadDistribution",
                      )}
                    </option>
                    <option value="strategic-partnership">
                      {t(
                        "affiliate.apply.referralMotionOptions.strategicPartnership",
                      )}
                    </option>
                    <option value="other">
                      {t("affiliate.apply.referralMotionOptions.other")}
                    </option>
                  </select>
                </div>

                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-primary">
                    {t("affiliate.apply.expectedMonthlyReferrals")}
                  </label>
                  <select
                    className="h-11 w-full rounded-xl border border-[#D6E0F3] bg-[#FCFDFE] px-3.5 text-sm"
                    value={form.expectedMonthlyReferrals}
                    onChange={(event) =>
                      setForm((prev) => ({
                        ...prev,
                        expectedMonthlyReferrals: event.target.value,
                      }))
                    }
                  >
                    <option value="0-5">0–5</option>
                    <option value="5-20">5–20</option>
                    <option value="20-50">20–50</option>
                    <option value="50+">50+</option>
                  </select>
                </div>

                <div className="space-y-1.5 md:col-span-2">
                  <label className="text-sm font-medium text-primary">
                    {t("affiliate.apply.notes")}
                  </label>
                  <Textarea
                    className="min-h-32 rounded-2xl border-[#D6E0F3] bg-[#FCFDFE] px-3.5 py-3"
                    value={form.notes}
                    onChange={(event) =>
                      setForm((prev) => ({
                        ...prev,
                        notes: event.target.value,
                      }))
                    }
                  />
                </div>
              </div>

              <div className="flex flex-col gap-3 border-t border-[#E5ECF8] px-5 py-5 md:flex-row md:items-center md:justify-between lg:px-7">
                <p className="text-xs text-muted-foreground">
                  {t("affiliate.apply.footerNote")}
                </p>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="min-w-[190px] bg-accent text-accent-foreground hover:bg-accent/90"
                >
                  {submitting
                    ? t("common.sending")
                    : t("affiliate.apply.sendApplication")}
                </Button>
              </div>
            </form>

            <aside className="rounded-[1.5rem] border border-[#D7E1F4] bg-white p-6 shadow-[0_20px_60px_-45px_rgba(12,31,79,0.28)]">
              <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-accent">
                {t("affiliate.apply.reviewPanelLabel")}
              </p>
              <h3 className="mt-3 text-lg font-semibold text-primary">
                {t("affiliate.apply.reviewPanelTitle")}
              </h3>
              <ul className="mt-4 space-y-3">
                {[
                  t("affiliate.apply.reviewPoint1"),
                  t("affiliate.apply.reviewPoint2"),
                  t("affiliate.apply.reviewPoint3"),
                ].map((item) => (
                  <li
                    key={item}
                    className="flex items-start gap-2 text-sm leading-6 text-muted-foreground"
                  >
                    <ArrowRight className="mt-1 h-4 w-4 shrink-0 text-accent" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </aside>
          </div>

          {submitted ? (
            <div className="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-4 shadow-[0_14px_34px_-24px_rgba(22,163,74,0.35)]">
              <div className="flex items-start gap-3">
                <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
                  <BadgeCheck className="h-4 w-4" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-emerald-900">
                    {t("affiliate.apply.successTitle")}
                  </p>
                  <p className="mt-1 text-sm leading-6 text-emerald-800/90">
                    {t("affiliate.apply.success")}
                  </p>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </section>
    </PublicSiteLayout>
  );
}
